import { useState, useEffect, useCallback } from 'react';
import { useConnector } from '../contexts/ConnectorContext';
import type {
  Order, OrderLineItem, OrderApprovalLog, OrderComment,
  NewApprovalData, NewCommentData, OrderData, OrderAttachment, AttachmentCategory,
} from '../types/ordering.types';

interface OrderDetailState {
  order: Order | null;
  lineItems: OrderLineItem[];
  approvalLog: OrderApprovalLog[];
  comments: OrderComment[];
  attachments: OrderAttachment[];
  isLoading: boolean;
  error: Error | null;
}

export function useOrderDetail(orderId: string | null) {
  const connector = useConnector();
  const [state, setState] = useState<OrderDetailState>({
    order: null, lineItems: [], approvalLog: [], comments: [], attachments: [],
    isLoading: false, error: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(() => {
    if (!orderId) return;
    setState(s => ({ ...s, isLoading: true, error: null }));
    connector.getOrderById(orderId)
      .then(async order => {
        const [lineItems, approvalLog, comments, attachments] = await Promise.all([
          connector.getOrderLineItems(order.spId),
          connector.getOrderApprovalLog(order.spId),
          connector.getOrderComments(order.spId),
          connector.getOrderAttachments(order.orderRef),
        ]);
        setState({ order, lineItems, approvalLog, comments, attachments, isLoading: false, error: null });
      })
      .catch(err => setState(s => ({ ...s, isLoading: false, error: err instanceof Error ? err : new Error(String(err)) })));
  }, [connector, orderId]);

  useEffect(() => { load(); }, [load]);

  const addComment = useCallback(async (data: NewCommentData) => {
    setIsSaving(true);
    try {
      const comment = await connector.addOrderComment(data);
      setState(s => ({ ...s, comments: [...s.comments, comment] }));
    } finally {
      setIsSaving(false);
    }
  }, [connector]);

  const addApproval = useCallback(async (data: NewApprovalData) => {
    setIsSaving(true);
    try {
      const log = await connector.addApprovalDecision(data);
      setState(s => ({ ...s, approvalLog: [...s.approvalLog, log] }));
      // Reload order to get updated status
      if (orderId) {
        const updated = await connector.getOrderById(orderId);
        setState(s => ({ ...s, order: updated }));
      }
    } finally {
      setIsSaving(false);
    }
  }, [connector, orderId]);

  const updateOrder = useCallback(async (data: Partial<OrderData>) => {
    if (!orderId) return;
    setIsSaving(true);
    try {
      await connector.updateOrder(orderId, data);
      const updated = await connector.getOrderById(orderId);
      setState(s => ({ ...s, order: updated }));
    } finally {
      setIsSaving(false);
    }
  }, [connector, orderId]);

  /** Upload a file to the order's folder in the "Order Attachments" library. */
  const uploadAttachment = useCallback(async (
    order: Order,
    file: File,
    category: AttachmentCategory
  ): Promise<OrderAttachment> => {
    setIsSaving(true);
    try {
      const att = await connector.uploadOrderAttachment(order.spId, order.orderRef, file, category);
      setState(s => ({ ...s, attachments: [...s.attachments, att] }));
      return att;
    } finally {
      setIsSaving(false);
    }
  }, [connector]);

  /** Download a file via the Bearer token and trigger a browser Save-As. */
  const downloadAttachment = useCallback(async (att: OrderAttachment): Promise<void> => {
    const blob = await connector.downloadOrderAttachment(att.serverRelUrl);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = att.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [connector]);

  return {
    ...state,
    isSaving,
    reload: load,
    addComment,
    addApproval,
    updateOrder,
    uploadAttachment,
    downloadAttachment,
  };
}
